import React, { useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import styles from "assets/jss/material-dashboard-pro-react/views/Commission/create.js";
import PropTypes from "prop-types";
import { Redirect, useParams } from "react-router-dom";
import { bookingApi } from "functions/apis";
import GridContainer from "components/Grid/GridContainer";
import GridItem from "components/Grid/GridItem";
import Card from "components/Card/Card";
import CardHeader from "components/Card/CardHeader";
import CardIcon from "components/Card/CardIcon";
import { Check, MailOutline } from "@material-ui/icons";
import CardBody from "components/Card/CardBody";
import Button from "components/CustomButtons/Button.js";
import CustomInput from "components/CustomInput/CustomInput";
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel
} from "@material-ui/core";
import Datetime from "react-datetime";
import { tripDateFormat } from "functions/Helper";

const useStyles = makeStyles(styles);

export default function EditAgentBookingPromoCode(props) {
  const [redirect, setRedirect] = useState(false);
  const [body, setBody] = useState({
    agentID: null,
    promoCode: null,
    effectiveFrom: null,
    effectiveUntil: null,
    discountAmount: null,
    minimumPax: null,
    maximumPax: null,
    onlyForRoundTrip: false,
    hasSectorRestriction: false,
    limit: 0,
    allowedSectorID: null,
    agent: null
  });
  const [disabled, setDisabled] = useState(false);
  const [agent, setAgent] = React.useState(null);

  const { id } = useParams();

  const EditAgentBookingPromoCodeSignal = React.useRef(new AbortController());

  const getData = () => {
    bookingApi
      .get("/AgentBookingPromoCodes/" + id, {
        signal: EditAgentBookingPromoCodeSignal.current.signal
      })
      .then(data => {
        setAgent(data.agent);
        setBody({
          agentID: data.agent.id,
          allowedSectorID: data.allowedSector ? data.allowedSector.id : null,
          discountAmount: data.discountAmount,
          effectiveFrom: new Date(data.effectiveFrom + "Z"),
          effectiveUntil: new Date(data.effectiveUntil + "Z"),
          hasSectorRestriction: data.hasSectorRestriction,
          limit: data.limit,
          minimumPax: data.minimumPax,
          maximumPax: data.maximumPax,
          onlyForRoundTrip: data.onlyForRoundTrip,
          promoCode: data.promoCode,
          agent: data.agent
        });
      })
      .catch(err => console.log(err));
  };

  useEffect(() => {
    getData();

    return () => EditAgentBookingPromoCodeSignal.current.abort();
  }, []);

  const handlePromoCodeChanged = e =>
    setBody({
      ...body,
      promoCode: e.target.value === null ? "" : e.target.value
    });

  const handleEffectiveFromChanged = e =>
    setBody({ ...body, effectiveFrom: e === "" ? null : e });

  const handleEffectiveUntilChanged = e =>
    setBody({ ...body, effectiveUntil: e === "" ? null : e });

  const handleDiscountAmountChanged = e =>
    setBody({
      ...body,
      discountAmount: e.target.value === "" ? null : e.target.value
    });

  const handleMinimumPaxChanged = e =>
    setBody({
      ...body,
      minimumPax: e.target.value === "" ? null : e.target.value
    });

  const handleMaximumPaxChanged = e =>
    setBody({
      ...body,
      maximumPax: e.target.value === "" ? null : e.target.value
    });

  const handleLimitChanged = e =>
    setBody({ ...body, limit: e.target.value === "" ? null : e.target.value });

  const handleOnlyForRoundTripChanged = e =>
    setBody({ ...body, onlyForRoundTrip: e.target.checked });

  const handleHasSectorRestrictionChanged = e =>
    setBody({ ...body, hasSectorRestriction: e.target.checked });

  const handleSubmit = e => {
    e.preventDefault();
    setDisabled(true);
    bookingApi
      .put("/AgentBookingPromoCodes/" + id, body, {
        signal: EditAgentBookingPromoCodeSignal.current.signal
      })
      .then(() => setRedirect(true))
      .catch(() => setDisabled(false));
  };

  const classes = useStyles();

  return redirect ? (
    <Redirect to={"/admin/AgentBookingPromoCodeList"} />
  ) : (
    body !== null && (
      <GridContainer>
        <GridItem xs={12} sm={12} md={12}>
          <Card>
            <CardHeader color="rose" icon>
              <CardIcon color="rose">
                <MailOutline />
              </CardIcon>
              <h4 className={classes.cardIconTitle}>
                EDIT AGENT BOOKING PROMO CODES
              </h4>
            </CardHeader>
            <CardBody>
              <Button onClick={() => props.history.goBack()} value="Back">
                BACK
              </Button>
              <form onSubmit={handleSubmit}>
                <GridContainer>
                  <GridItem xs={12} md={6}>
                    <CustomInput
                      labelText="Agent"
                      disabled
                      formControlProps={{
                        fullWidth: true
                      }}
                      inputProps={{
                        value: agent ? agent.name : "",
                        disabled: true
                      }}
                    />
                  </GridItem>
                  <GridItem xs={12} md={6}>
                    <CustomInput
                      labelText="Promo Code"
                      formControlProps={{
                        fullWidth: true
                      }}
                      disabled
                      inputProps={{
                        onChange: handlePromoCodeChanged,
                        value: body.promoCode ?? "",
                        disabled: true
                      }}
                    />
                  </GridItem>
                  <GridItem xs={12} md={6}>
                    <CustomInput
                      labelText="Currency"
                      formControlProps={{
                        fullWidth: true
                      }}
                      disabled
                      inputProps={{
                        value:
                          body.agent && body.agent.billingCurrency
                            ? body.agent.billingCurrency.code
                            : "",
                        disabled: true
                      }}
                    />
                  </GridItem>
                  <GridItem xs={12} md={6}>
                    <CustomInput
                      labelText="Discount Amount"
                      formControlProps={{
                        fullWidth: true
                      }}
                      disabled
                      inputProps={{
                        type: "number",
                        onChange: handleDiscountAmountChanged,
                        value: body.discountAmount ?? "",
                        disabled: true
                      }}
                    />
                  </GridItem>
                  <GridItem xs={12} md={6}>
                    <CustomInput
                      labelText="Minimum Pax"
                      formControlProps={{
                        fullWidth: true
                      }}
                      disabled
                      inputProps={{
                        type: "number",
                        onChange: handleMinimumPaxChanged,
                        value: body.minimumPax ?? "",
                        disabled: true
                      }}
                    />
                  </GridItem>
                  <GridItem xs={12} md={6}>
                    <CustomInput
                      labelText="Maximum Pax"
                      formControlProps={{
                        fullWidth: true
                      }}
                      disabled
                      inputProps={{
                        type: "number",
                        onChange: handleMaximumPaxChanged,
                        value: body.maximumPax ?? "",
                        disabled: true
                      }}
                    />
                  </GridItem>
                  <GridItem xs={12} md={6}>
                    <CustomInput
                      labelText="Limit"
                      formControlProps={{
                        fullWidth: true
                      }}
                      inputProps={{
                        type: "number",
                        onChange: handleLimitChanged,
                        value: body.limit
                      }}
                    />
                  </GridItem>
                  <GridItem xs={12} md={6}>
                    <InputLabel className={classes.selectLabel}>
                      Effective From
                    </InputLabel>
                    <FormControl fullWidth>
                      <Datetime
                        value={body.effectiveFrom}
                        onChange={handleEffectiveFromChanged}
                        dateFormat={tripDateFormat}
                        timeFormat
                        closeOnSelect
                        inputProps={{
                          placeholder: "Effective From"
                        }}
                      />
                    </FormControl>
                  </GridItem>
                  <GridItem xs={12} md={6}>
                    <InputLabel className={classes.selectLabel}>
                      Effective Until
                    </InputLabel>
                    <FormControl fullWidth>
                      <Datetime
                        value={body.effectiveUntil}
                        onChange={handleEffectiveUntilChanged}
                        dateFormat={tripDateFormat}
                        timeFormat
                        closeOnSelect
                        inputProps={{
                          placeholder: "Effective Until"
                        }}
                      />
                    </FormControl>
                  </GridItem>
                  <GridItem xs={12} md={12}>
                    <FormControlLabel
                      disabled
                      control={
                        <Checkbox
                          checked={body.onlyForRoundTrip}
                          onChange={handleOnlyForRoundTripChanged}
                          checkedIcon={
                            <Check className={classes.checkedIcon} />
                          }
                          icon={<Check className={classes.uncheckedIcon} />}
                          classes={{
                            checked: classes.checked,
                            root: classes.checkRoot
                          }}
                          disabled
                        />
                      }
                      classes={{
                        label: classes.label,
                        root: classes.labelRoot
                      }}
                      label="Only For Round Trip"
                    />
                  </GridItem>
                  <GridItem xs={12} md={12}>
                    <FormControlLabel
                      disabled
                      control={
                        <Checkbox
                          checked={body.hasSectorRestriction}
                          onChange={handleHasSectorRestrictionChanged}
                          checkedIcon={
                            <Check className={classes.checkedIcon} />
                          }
                          icon={<Check className={classes.uncheckedIcon} />}
                          classes={{
                            checked: classes.checked,
                            root: classes.checkRoot
                          }}
                          disabled
                        />
                      }
                      classes={{
                        label: classes.label,
                        root: classes.labelRoot
                      }}
                      label="Has Sector Restriction"
                    />
                  </GridItem>
                  {body.hasSectorRestriction && (
                    <GridItem xs={12} md={12}>
                      <CustomInput
                        labelText="ALLOWED ROUTES"
                        disabled
                        formControlProps={{
                          fullWidth: true
                        }}
                        inputProps={{
                          value: body.allowedSector
                            ? body.allowedSector.name
                            : "",
                          disabled: true
                        }}
                      />
                    </GridItem>
                  )}
                  <GridItem xs={12} md={12}>
                    <Button
                      disabled={disabled}
                      color="rose"
                      type="submit"
                      value="Submit"
                    >
                      UPDATE
                    </Button>
                  </GridItem>
                </GridContainer>
              </form>
            </CardBody>
          </Card>
        </GridItem>
      </GridContainer>
    )
  );
}

EditAgentBookingPromoCode.propTypes = {
  history: PropTypes.object
};
